/* Capacitaciones Administrativas - listado del anfitrión (TD-11629).
 * Cada anfitrión ve solo las capacitaciones que creó.
 */
import { IonBadge, IonButton, IonContent, IonIcon, IonPage } from "@ionic/react";
import { CircularProgress } from "@material-ui/core";
import { addOutline, calendarOutline, peopleOutline, schoolOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Header from "src/components/Header";
import CapacitacionAdmAPI, { CapacitacionResumen, EstadoCapacitacion } from "src/services/capacitacionAdm";
import { ESTADO_CAPACITACION, ETIQUETA_MODALIDAD, formatFechaCorta } from "src/utils/capacitacionAdm";
import "./CapacitacionAdm.css";

type Filtro = "ACTIVAS" | EstadoCapacitacion;

const FILTROS: Array<{ valor: Filtro; etiqueta: string }> = [
  { valor: "ACTIVAS", etiqueta: "Activas" },
  { valor: "BORRADOR", etiqueta: "Borradores" },
  { valor: "ABIERTA", etiqueta: "Registro abierto" },
  { valor: "CERRADA", etiqueta: "Registro cerrado" },
  { valor: "ANULADA", etiqueta: "Anuladas" },
];

const CapacitacionesAdm: React.FC = () => {
  const history = useHistory();
  const [cargando, setCargando] = useState(true);
  const [lista, setLista] = useState<CapacitacionResumen[]>([]);
  const [filtro, setFiltro] = useState<Filtro>("ACTIVAS");

  // Las rutas van en un <Switch>: la página se vuelve a montar al volver del detalle.
  useEffect(() => {
    CapacitacionAdmAPI.listar()
      .then(setLista)
      .catch(() => setLista([]))
      .finally(() => setCargando(false));
  }, []);

  const visibles = useMemo(
    () => lista.filter((c) => (filtro === "ACTIVAS" ? c.estado !== "ANULADA" : c.estado === filtro)),
    [lista, filtro]
  );

  return (
    <IonPage>
      <Header titulo="Capacitaciones administrativas" />
      <IonContent id="capacitaciones-adm">
        <div className="capadm-page capadm-scope campos-mui">
          <IonButton expand="block" routerLink="/capacitaciones-adm/nueva">
            <IonIcon slot="start" icon={addOutline} /> Nueva capacitación
          </IonButton>

          {cargando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : lista.length === 0 ? (
            <div className="capadm-vacio">
              <IonIcon icon={schoolOutline} />
              Todavía no creaste capacitaciones.
              <br />
              Podés prepararla como borrador y publicarla cuando esté lista.
            </div>
          ) : (
            <>
              <div className="capadm-filtros" style={{ marginTop: 16 }}>
                {FILTROS.map((f) => (
                  <button
                    key={f.valor}
                    type="button"
                    className={`capadm-filtro${filtro === f.valor ? " activo" : ""}`}
                    onClick={() => setFiltro(f.valor)}
                  >
                    {f.etiqueta}
                  </button>
                ))}
              </div>

              {visibles.length === 0 && <div className="capadm-vacio">No hay capacitaciones en este filtro.</div>}

              {visibles.map((c) => (
                <div
                  key={c.id}
                  className="capadm-card"
                  role="button"
                  style={{ cursor: "pointer" }}
                  onClick={() => history.push(`/capacitaciones-adm/${c.id}`)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <p className="capadm-card-titulo">{c.tema}</p>
                    <IonBadge color={ESTADO_CAPACITACION[c.estado].color} style={{ alignSelf: "flex-start" }}>
                      {ESTADO_CAPACITACION[c.estado].etiqueta}
                    </IonBadge>
                  </div>
                  <div className="capadm-meta">
                    <span>
                      <IonIcon icon={calendarOutline} /> {formatFechaCorta(c.fecha_capacitacion)}
                    </span>
                    <span>{ETIQUETA_MODALIDAD[c.modalidad]}</span>
                    <span>
                      <IonIcon icon={peopleOutline} /> {c.confirmados} confirmado(s)
                      {c.pendientes > 0 ? ` · ${c.pendientes} pendiente(s)` : ""}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionesAdm;
